import React from 'react';
import styled from 'styled-components';
import { CBanner } from '@appcues/component-library';
import { ExternalLink } from '@studio/legacy-components';
import GuideStep from 'components/InstallationGuide/GuideStep';
import GuideHeading from 'components/InstallationGuide/GuideHeading';
import GuideParagraph from 'components/InstallationGuide/GuideParagraph';
import CopyableCodeSnippet from 'components/Common/CopyableCodeSnippet';
import {
  IDENTIFY_SNIPPETS,
  HTML,
  SYNTAX_HIGHLIGHTING_CLASS,
} from 'constants/installation/snippets';
import {
  ANONYMOUS_USERS_DOC,
  INSTALL_TEMPLATE_LINK,
  TECHNICAL_EVENTS_DOC_URL,
  TECHNICAL_PROPS_DOC_URL,
} from 'constants/externalAppcuesResources';

const InstallPropsAndEvents = props => {
  const {
    moveForward,
    moveBackward,
    selectedInstallFramework,
    stepMax,
    stepIndex,
  } = props;

  const identifyScript =
    IDENTIFY_SNIPPETS[selectedInstallFramework] || IDENTIFY_SNIPPETS[HTML];

  const identifyLanguage =
    SYNTAX_HIGHLIGHTING_CLASS[selectedInstallFramework] ||
    SYNTAX_HIGHLIGHTING_CLASS[HTML];

  return (
    <GuideStep
      stepIndex={stepIndex}
      stepMax={stepMax}
      title="Identify users and track events"
      moveForward={moveForward}
      moveBackward={moveBackward}
    >
      <GuideHeading>Why</GuideHeading>
      <GuideParagraph>
        Including properties for users (e.g. email address) and events they
        perform (e.g. clicking a button) will enable more accurate targeting.
      </GuideParagraph>
      <StyledCBanner>
        <div>
          If you&apos;re installing Appcues for someone, make sure you ask them
          what <strong>user properties and events</strong> they need. If they
          filled out{' '}
          <ExternalLink href={INSTALL_TEMPLATE_LINK}>a template</ExternalLink>{' '}
          you can use it as a reference.
        </div>
      </StyledCBanner>
      <GuideHeading>How to identify users</GuideHeading>
      <GuideParagraph>
        The <code>Appcues.identify()</code> call is used on each page to
        identify the user and determine if they should see Appcues content. The
        user ID (a unique identifier) is required. Additional properties can
        also be included.{' '}
        <ExternalLink href={TECHNICAL_PROPS_DOC_URL}>Learn more</ExternalLink>
      </GuideParagraph>
      <CopyableCodeSnippet language={identifyLanguage}>
        {identifyScript}
      </CopyableCodeSnippet>
      <GuideParagraph>
        Appcues targeting requires identifying users for each page visit. If
        your application doesn’t identify users check out{' '}
        <ExternalLink href={ANONYMOUS_USERS_DOC}>this doc.</ExternalLink>
      </GuideParagraph>
      <GuideHeading>How to track events</GuideHeading>
      <GuideParagraph>
        Use the <code>Appcues.track()</code> call to track actions users take in
        your application.{' '}
        <ExternalLink href={TECHNICAL_EVENTS_DOC_URL}>Learn more</ExternalLink>
      </GuideParagraph>
      <GuideParagraph>
        If you use an analytics tool (e.g. Mixpanel, Amplitude, Hotjar,
        FullStory, Google Analytics) add the <code>Appcues.track()</code>{' '}
        wherever you’re already tracking events.
      </GuideParagraph>
      <CopyableCodeSnippet>
        {`
          Appcues.track("Event Name");

          // e.g. Clicked the purchase button
          Appcues.track("Clicked purchase button");

          // e.g. Submitted a help ticket
          Appcues.track("Submitted help ticket", {
            url: "/support",
            article: "installation"
          });
        `}
      </CopyableCodeSnippet>
    </GuideStep>
  );
};

const StyledCBanner = styled(CBanner)`
  max-width: 80%;
`;

export default InstallPropsAndEvents;
